import React from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Select from "react-select";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select as MuiSelect,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  Award,
  Calendar,
  CheckCircle2,
  Church,
  DollarSign,
  Edit3,
  FileText,
  Globe,
  MapPin,
  Plus,
  RefreshCw,
  Rocket,
  ShieldCheck,
  Trash2,
  TrendingUp,
  Users,
} from "lucide-react";
import { formatCoupleOptionLabel } from "../../../../../utils/coupleLabels";
import { formatParishOptionLabel } from "../../../../../utils/parishLabels";
import {
  BUDGET_LINE_KEYS,
  ELIGIBILITY_ITEMS,
  FUNDING_STATUSES,
  RETREAT_TYPES,
  SEASONS,
  SUPPORT_LABELS,
  SUPPORT_TYPES,
  type Season,
  type SupportTypeCode,
} from "../projectedRetreatConstants";
import {
  coupleEmails,
  coupleShortName,
  formatCurrency,
  formatWeekendRange,
  parishAddressLine,
  parishDisplayName,
} from "../projectedRetreatHelpers";
import {
  SectionBlock,
  fieldSx,
  PROJECTED_NAVY,
  PROJECTED_NAVY_TEXT,
  projectedSaveButtonSx,
  HandoffSummaryCard,
  HANDOFF_PANEL_BG,
  HANDOFF_PANEL_BORDER,
} from "../projectedRetreatUi";
import type { ProjectedRetreatFormModel } from "../useProjectedRetreatForm";
import type { useProjectedActionBarPin } from "../useProjectedActionBarPin";

type FormModel = ProjectedRetreatFormModel;
type PinModel = ReturnType<typeof useProjectedActionBarPin>;

function findNestedScrollParent(node: HTMLElement): HTMLElement | null {
  let parent = node.parentElement;
  while (
    parent &&
    parent !== document.body &&
    parent !== document.documentElement
  ) {
    const oy = getComputedStyle(parent).overflowY;
    if (
      (oy === "auto" || oy === "scroll") &&
      parent.scrollHeight > parent.clientHeight + 1
    ) {
      return parent;
    }
    parent = parent.parentElement;
  }
  return null;
}

function stickySectionOffset(pin?: PinModel): number {
  const header = document.querySelector(".sing-header") as HTMLElement | null;
  const headerBottom = header
    ? Math.max(0, Math.round(header.getBoundingClientRect().bottom))
    : 0;
  const barHeight = pin?.actionBarPin.height ?? 56;
  const pinGap = pin?.actionBarPinned ? 8 : 0;
  return headerBottom + pinGap + barHeight + 16;
}

export function ProjectedRetreatFormSections({
  form,
  pin,
}: {
  form: FormModel;
  pin?: PinModel;
}) {
  const {
    navigate,
    retreatId,
    saving,
    transitioning,
    parishes,
    couples,
    parishId,
    setParishId,
    venue,
    setVenue,
    language,
    setLanguage,
    retreatType,
    season,
    year,
    tentativeFriday,
    tentativeSunday,
    dateError,
    calendarOpenToDate,
    supportType,
    setSupportType,
    pendingContactValue,
    setPendingContactValue,
    planningSupport,
    setPlanningSupport,
    planningNotes,
    setPlanningNotes,
    seedStatus,
    setSeedStatus,
    seedAmount,
    setSeedAmount,
    seedFundingContact,
    setSeedFundingContact,
    seedExecutionContact,
    setSeedExecutionContact,
    seedNote,
    setSeedNote,
    sponsorshipStatus,
    setSponsorshipStatus,
    sponsorshipContact,
    setSponsorshipContact,
    sponsorshipExecution,
    setSponsorshipExecution,
    sponsorshipNote,
    setSponsorshipNote,
    budgetNote,
    setBudgetNote,
    budget,
    setBudget,
    leadCoupleId,
    setLeadCoupleId,
    eligibility,
    setEligibility,
    nextStage,
    setNextStage,
    transitionNote,
    setTransitionNote,
    notificationMessage,
    setNotificationMessage,
    setNotificationEdited,
    dirty,
    newParishModalOpen,
    setNewParishModalOpen,
    newParishSaving,
    newParishForm,
    setNewParishForm,
    newParishErrors,
    setNewParishErrors,
    leadContactModalOpen,
    setLeadContactModalOpen,
    leadContactSaving,
    leadContactForm,
    setLeadContactForm,
    reportModalOpen,
    setReportModalOpen,
    setupConfirmModalOpen,
    setSetupConfirmModalOpen,
    transitionSuccess,
    selectedParish,
    selectedLead,
    leadAssigned,
    coupleOptions,
    contactOptions,
    oversightOptions,
    oversightPickerHelper,
    oversightPickerPlaceholder,
    oversightSubtitle,
    showSeedFunding,
    showOutreachBudget,
    budgetTotal,
    eligibilityCount,
    eligibilityComplete,
    primarySupport,
    primarySupportName,
    resolveSupportName,
    resolveSupportEmail,
    planningReadiness,
    transitionMissing,
    markDirty,
    applyRetreatType,
    handleFridayChange,
    handleSeasonChange,
    handleYearChange,
    rebuildNotification,
    handleSave,
    handleAddSupport,
    handleSaveNewParish,
    openLeadContactEdit,
    handleSaveLeadContact,
    handleConfirmTransition,
    displayPercent,
    saveStatusLabel,
    saveStatusShort,
  } = form;

  const weekendLabel =
    tentativeFriday && tentativeSunday
      ? formatWeekendRange(tentativeFriday, tentativeSunday)
      : "Exact weekend remains tentative";
  const seasonYearLabel =
    season && year ? `${season} ${year}` : "";
  const leadEmail = coupleEmails(selectedLead) || "Email address unavailable";
  const planningEmail = primarySupport
    ? resolveSupportEmail(primarySupport) || "Email address unavailable"
    : "Email address unavailable";
  const recipientEmails =
    [coupleEmails(selectedLead), primarySupport ? resolveSupportEmail(primarySupport) : ""]
      .filter(Boolean)
      .join(" · ") || "Recipient contact information is unavailable.";

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    const offset = stickySectionOffset(pin);
    el.style.scrollMarginTop = `${offset}px`;

    const nested = findNestedScrollParent(el);
    if (nested) {
      const top =
        el.getBoundingClientRect().top -
        nested.getBoundingClientRect().top +
        nested.scrollTop -
        offset;
      nested.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
      return;
    }

    const se = (document.scrollingElement ||
      document.documentElement) as HTMLElement;
    const top = Math.max(
      0,
      el.getBoundingClientRect().top +
        (se.scrollTop || window.scrollY || document.body.scrollTop) -
        offset,
    );
    se.scrollTo({ top, behavior: "smooth" });
    if (document.body !== se) {
      document.body.scrollTo({ top, behavior: "smooth" });
    }
    window.scrollTo({ top, behavior: "smooth" });
  };

  return (
    <>
      {/* Page header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ sm: "flex-start" }}
        spacing={2}
        sx={{ mb: selectedParish ? 0 : 0 }}
      >
        <Box>
          <Typography
            variant="overline"
            sx={{
              color: "#rgb(33, 52, 73)",
              fontWeight: 800,
              letterSpacing: "0.02em",
              fontSize: "0.72rem",
              lineHeight: 1.15,
              display: "block",
              mb: 0,
            }}
          >
            Future Retreat planning
          </Typography>
          <Typography
            variant="h4"
            sx={{ fontWeight: 900, color: "#0f172a", letterSpacing: "-0.02em", fontSize: { xs: "1.6rem", sm: "2rem" } }}
          >
            Project a Future Retreat
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ color: "#64748b", mt: 0.5, fontSize: "0.95rem" }}>
            Place a future retreat on the planning horizon. Exact details can be completed over time.
          </Typography>
        </Box>
        <Stack
          spacing={0.75}
          sx={{ alignItems: { xs: "flex-start", sm: "flex-end" } }}
        >
          <Button
            variant="outlined"
            size="medium"
            startIcon={<FileText size={16} />}
            onClick={() => setReportModalOpen(true)}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 2.5,
              px: 2.5,
              py: 1,
              color: "#1e293b",
              borderColor: "#cbd5e1",
              bgcolor: "#ffffff",
              boxShadow: "0 2px 6px rgba(15, 23, 42, 0.04)",
              "&:hover": {
                borderColor: "#2563eb",
                bgcolor: "#eff6ff",
                color: "#1d4ed8",
              },
            }}
          >
            Preview Report
          </Button>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ maxWidth: 220, textAlign: { sm: "right" } }}
          >
            Preview now; save the Projected retreat before printing the official
            record.
          </Typography>
        </Stack>
      </Stack>

      {selectedParish && (
        <Paper
          component="section"
          variant="outlined"
          elevation={0}
          aria-label="Projected retreat identity"
          sx={{
            mt: 1,
            mb: 1.5,
            px: { xs: 1.5, sm: 2 },
            py: 1.25,
            bgcolor: "#ffffff",
            borderColor: "#e0e7ef",
            borderLeft: "4px solid",
            borderLeftColor: PROJECTED_NAVY,
            borderRadius: 1.5,
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={{ xs: 0.75, sm: 2 }}
            sx={{ alignItems: { sm: "center" }, justifyContent: "space-between" }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="overline"
                sx={{
                  display: "block",
                  color: PROJECTED_NAVY,
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  lineHeight: 1.2,
                  textTransform: "uppercase",
                }}
              >
                Projected retreat
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: PROJECTED_NAVY_TEXT,
                  lineHeight: 1.25,
                  mt: 0.15,
                }}
              >
                {parishDisplayName(selectedParish)}
              </Typography>
              <Stack
                direction="row"
                spacing={0.5}
                sx={{ alignItems: "center", mt: 0.25, color: "#64748b" }}
              >
                <MapPin size={16} strokeWidth={2} />
                <Typography variant="body2" sx={{ color: "#64748b" }}>
                  {[selectedParish.city, selectedParish.state]
                    .filter(Boolean)
                    .join(", ") || "No location on file"}
                </Typography>
              </Stack>
            </Box>
            <Chip
              label={language || "English"}
              variant="outlined"
              size="small"
              sx={{
                alignSelf: { xs: "flex-start", sm: "auto" },
                height: 28,
                bgcolor: "#ffffff",
                color: PROJECTED_NAVY,
                borderColor: PROJECTED_NAVY,
                fontWeight: 600,
                fontSize: "0.8125rem",
                borderRadius: "16px",
                "& .MuiChip-label": { px: 1.25 },
              }}
            />
          </Stack>
        </Paper>
      )}

      {transitionSuccess ? (
        <Alert severity="success" icon={<CheckCircle2 size={22} />} sx={{ mt: 2, mb: 3 }}>
          <Typography variant="h6">Transition Successful</Typography>
          <Typography>
            {transitionSuccess.parishName} is now in Retreats in Setup.
          </Typography>
          <Stack spacing={0.35} sx={{ mt: 1 }}>
            <Typography variant="body2">
              Same retreat ID retained: {transitionSuccess.retreatId}
            </Typography>
            <Typography variant="body2">
              Access assignment preparation recorded:{" "}
              {new Date(transitionSuccess.accessPreparedAt).toLocaleString()}
            </Typography>
            <Typography variant="body2">
              No access was granted or activated
            </Typography>
            <Typography variant="body2">
              Notification prepared; no production email was sent
            </Typography>
            <Typography variant="body2">Retreat History updated</Typography>
          </Stack>
        </Alert>
      ) : null}

      {/* Quick Jump Navigation Pills */}
      <Box sx={{ mb: 1, display: "flex", gap: 1, flexWrap: "wrap", overflowX: "auto", pb: 0.5 }}>
        {[
          ["section-parish", "Parish Details", <Church key="p" size={14} />],
          ["section-schedule", "Planning Window", <Calendar key="s" size={14} />],
          ...(showSeedFunding ? [["section-seed", "Seed Funding", <DollarSign key="f" size={14} />]] : []),
          ...(showOutreachBudget ? [["section-budget", "Outreach Budget", <TrendingUp key="b" size={14} />]] : []),
          ["section-oversight", "Planning Oversight", <Users key="o" size={14} />],
          ["section-leadership", "Future Leadership", <Award key="l" size={14} />],
          ...(leadAssigned && !transitionSuccess
            ? [["section-handoff", "Next Stage", <Rocket key="h" size={14} />]]
            : []),
        ].map(([id, label, icon]) => (
          <Chip
            key={id as string}
            icon={icon as React.ReactElement}
            label={label as string}
            onClick={() => scrollToSection(id as string)}
            clickable
            sx={{
              bgcolor: "#ffffff",
              border: "1px solid #e2e8f0",
              color: "#475569",
              fontWeight: 700,
              fontSize: "0.78rem",
              py: 1.75,
              px: 0.5,
              borderRadius: 2.5,
              boxShadow: "0 1px 3px rgba(15, 23, 42, 0.03)",
              transition: "all 0.15 ease",
              "&:hover": {
                bgcolor: "#eff6ff",
                borderColor: "#bfdbfe",
                color: "#1d4ed8",
              },
            }}
          />
        ))}
      </Box>

      <Box id="projected-retreat-form">
        <Stack spacing={3}>

        {/* Parish */}
        <SectionBlock
          id="section-parish"
          title="Parish & Facility Details"
          subtitle="Select a parish to use its official authoritative address. Add a venue only when the retreat location differs."
          icon={<Church size={20} />}
        >
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: {
                xs: "1fr",
                md: "minmax(280px, 0.9fr) minmax(320px, 1.1fr)",
              },
            }}
          >
            <Stack spacing={1.5}>
              <FormControl fullWidth size="small" required sx={fieldSx}>
                <InputLabel>Select Parish</InputLabel>
                <MuiSelect
                  label="Select Parish"
                  value={parishId === "" ? "" : String(parishId)}
                  onChange={(e) => {
                    setParishId(e.target.value ? Number(e.target.value) : "");
                    markDirty();
                  }}
                >
                  <MenuItem value="">
                    <em>Select a parish</em>
                  </MenuItem>
                  {parishes.map((p) => (
                    <MenuItem key={p.id} value={String(p.id)}>
                      {formatParishOptionLabel(p as any)}
                    </MenuItem>
                  ))}
                </MuiSelect>
                <FormHelperText>
                  Search by parish name or location.
                </FormHelperText>
              </FormControl>
              <Button
                startIcon={<Plus size={16} />}
                variant="outlined"
                size="small"
                sx={{
                  alignSelf: "flex-start",
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: 2,
                  borderColor: "#cbd5e1",
                  color: "#334155",
                  "&:hover": { borderColor: "#2563eb", bgcolor: "#eff6ff" },
                }}
                onClick={() => setNewParishModalOpen(true)}
              >
                Add New Parish
              </Button>
            </Stack>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: "#f8fafc",
                borderRadius: 2.5,
                border: "1px solid #e2e8f0",
                minHeight: 88,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <MapPin size={16} color="#2563eb" />
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 800, color: "#475569", letterSpacing: "0.04em", textTransform: "uppercase" }}
                >
                  Parish Address
                </Typography>
              </Stack>
              <Typography
                variant="body2"
                sx={{ mt: 0.75, fontWeight: 600, color: selectedParish ? "#0f172a" : "#64748b" }}
              >
                {selectedParish
                  ? parishAddressLine(selectedParish) || "No address on file for this parish."
                  : "Select a parish to view its authoritative address."}
              </Typography>
            </Paper>
          </Box>
          <TextField
            fullWidth
            size="small"
            label="Retreat Location / Venue (optional)"
            value={venue}
            onChange={(e) => {
              setVenue(e.target.value);
              markDirty();
            }}
            helperText="Use free text for a parish facility, retreat center, or alternate location."
            sx={{ ...fieldSx, mt: 2.5 }}
          />
        </SectionBlock>

        {/* Retreat & Planning Window */}
        <SectionBlock
          id="section-schedule"
          title="Retreat & Planning Window"
          subtitle="A tentative Friday start is optional; its implied weekend remains planning-only until Setup."
          icon={<Calendar size={20} />}
        >
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 1fr",
                lg: "minmax(130px, 0.7fr) minmax(230px, 1.3fr) minmax(120px, 0.65fr) 110px minmax(200px, 0.9fr)",
              },
              alignItems: "start",
            }}
          >
            <FormControl fullWidth size="small" required sx={fieldSx}>
              <InputLabel>Language</InputLabel>
              <MuiSelect
                label="Language"
                value={language}
                onChange={(e) => {
                  setLanguage(String(e.target.value));
                  markDirty();
                }}
              >
                <MenuItem value="English">English</MenuItem>
                <MenuItem value="Spanish">Spanish</MenuItem>
              </MuiSelect>
            </FormControl>
            <FormControl fullWidth size="small" required sx={fieldSx}>
              <InputLabel>Retreat Type</InputLabel>
              <MuiSelect
                label="Retreat Type"
                value={retreatType}
                onChange={(e) => applyRetreatType(String(e.target.value))}
              >
                {RETREAT_TYPES.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </MuiSelect>
              <FormHelperText>Sets planning defaults.</FormHelperText>
            </FormControl>
            <FormControl
              fullWidth
              size="small"
              required
              sx={fieldSx}
            >
              <InputLabel>Season</InputLabel>
              <MuiSelect
                label="Season"
                value={season}
                onChange={(e) => {
                  handleSeasonChange(e.target.value as Season);
                }}
              >
                {SEASONS.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </MuiSelect>
              <FormHelperText>
                {tentativeFriday
                  ? "Based on Tentative Start Date."
                  : "Planning estimate while the exact date is unknown."}
              </FormHelperText>
            </FormControl>
            <TextField
              fullWidth
              size="small"
              required
              type="number"
              label="Year"
              value={year}
              onChange={(e) => {
                handleYearChange(e.target.value);
              }}
              helperText=       {tentativeFriday
                  ? "Based on Tentative Start Date."
                  : "Planning estimate while the exact date is unknown."}      
              sx={fieldSx}
            />
            <Box
              sx={{
                width: "100%",
                "& .react-datepicker-wrapper": { width: "100%", display: "block" },
                "& .react-datepicker__input-container": {
                  width: "100%",
                  display: "block",
                  position: "relative",
                },
                "& .react-datepicker__input-container input.form-select": {
                  paddingRight: "3.25rem",
                },
                "& .react-datepicker__close-icon": {
                  right: "1.75rem",
                  padding: 0,
                  height: "100%",
                },
                "& .react-datepicker__close-icon::after": {
                  fontSize: 14,
                  height: 16,
                  width: 16,
                  padding: 0,
                  lineHeight: "14px",
                },
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mb: 0.5,
                  fontWeight: 700,
                  color: "#475569",
                  fontSize: "0.78rem",
                }}
              >
                Tentative Start Date
              </Typography>
              <DatePicker
                key={`friday-cal-${season}-${year}`}
                autoComplete="off"
                dateFormat="MM-dd-yyyy"
                placeholderText="mm-dd-yyyy (Friday)"
                className="form-select"
                wrapperClassName="w-100"
                selected={tentativeFriday}
                onChange={handleFridayChange}
                openToDate={calendarOpenToDate}
                filterDate={(date) => date.getDay() === 5}
                isClearable
              />
              <FormHelperText error={Boolean(dateError)}>
                {dateError || "Optional. Select a Friday."}
              </FormHelperText>
            </Box>
          </Box>
          <Paper
            elevation={0}
            sx={{
              mt: 2.5,
              p: 2,
              bgcolor: "#f8fafc",
              borderRadius: 2.5,
              border: "1px solid #e2e8f0",
              borderLeft: "4px solid #2563eb",
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 700, color: "#0f172a" }}>
              Projected Retreat Weekend:{" "}
              <Typography
                component="span"
                variant="body2"
                sx={{ color: tentativeFriday && tentativeSunday ? "#1d4ed8" : "#64748b", fontWeight: 700 }}
              >
                {tentativeFriday && tentativeSunday
                  ? formatWeekendRange(tentativeFriday, tentativeSunday)
                  : "Exact weekend not yet selected"}
              </Typography>
            </Typography>
            {tentativeSunday && (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mt: 0.5, color: "#64748b" }}
              >
                Sunday derived automatically:{" "}
                {tentativeSunday.toLocaleDateString("en-US", {
                  weekday: "long",
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                })}
              </Typography>
            )}
          </Paper>
        </SectionBlock>

        {/* Conditional finance */}
        {showSeedFunding && (
          <SectionBlock
            id="section-seed"
            title="First-Retreat Seed Funding"
            subtitle="Capture planning status, decision ownership, and tentative amounts without treating a proposal as approval."
            icon={<DollarSign size={20} />}
          >
            <Alert severity="info" sx={{ mb: 2, borderRadius: 2.5 }}>
              Use this section to coordinate possible first-retreat seed
              funding. The form does not promise or approve funds.
            </Alert>
            {language === "Spanish" && (
              <Alert severity="warning" sx={{ mb: 2, borderRadius: 2.5 }}>
                Policy check: Executive Committee minutes dated February 5,
                2025 ended seed money for a parish starting retreats in a
                second language. Confirm whether this is the parish&apos;s
                first retreat or a later language expansion.
              </Alert>
            )}
            <Box
              sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              }}
            >
              <FormControl fullWidth size="small" sx={fieldSx}>
                <InputLabel>Seed funding status</InputLabel>
                <MuiSelect
                  label="Seed funding status"
                  value={seedStatus}
                  onChange={(e) => {
                    setSeedStatus(String(e.target.value));
                    markDirty();
                  }}
                >
                  {FUNDING_STATUSES.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </MuiSelect>
                <FormHelperText>
                  Record the present planning state; do not assume approval.
                </FormHelperText>
              </FormControl>
              <TextField
                fullWidth
                size="small"
                label="Tentative seed donation amount"
                value={seedAmount}
                onChange={(e) => {
                  setSeedAmount(e.target.value);
                  markDirty();
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
                helperText="Leave blank until an amount is proposed or confirmed."
                sx={fieldSx}
              />
              <TextField
                fullWidth
                size="small"
                label="Seed money point of contact"
                value={seedFundingContact}
                onChange={(e) => {
                  setSeedFundingContact(e.target.value);
                  markDirty();
                }}
                helperText="Who confirms availability and approval?"
                sx={fieldSx}
              />
              <TextField
                fullWidth
                size="small"
                label="Execution / disbursement contact"
                value={seedExecutionContact}
                onChange={(e) => {
                  setSeedExecutionContact(e.target.value);
                  markDirty();
                }}
                helperText="Who coordinates payment or reimbursement after approval?"
                sx={fieldSx}
              />
            </Box>
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={2}
              label="Funding coordination note"
              value={seedNote}
              onChange={(e) => {
                setSeedNote(e.target.value);
                markDirty();
              }}
              helperText="Record the conversation, decision still needed, owner, and next follow-up."
              sx={{ ...fieldSx, mt: 2 }}
            />
          </SectionBlock>
        )}

        {showOutreachBudget && (
          <SectionBlock
            id="section-budget"
            title="Outreach Sponsorship & Proposed Budget"
            subtitle="Capture planning status, decision ownership, and tentative amounts without treating a proposal as approval."
            icon={<TrendingUp size={20} />}
          >
            <Alert severity="info" sx={{ mb: 2, borderRadius: 2.5 }}>
              Build a tentative sponsorship plan here. Proposed amounts do
              not authorize spending or confirm coverage.
            </Alert>
            <Alert severity="warning" sx={{ mb: 2, borderRadius: 2.5 }}>
              Historical reference: the February 5, 2025 Executive Committee
              minutes recorded a 50/50 retreat donation percentage for
              pro-bono Archbishop Outreach retreats. Confirm that guidance is
              still current for this retreat.
            </Alert>
            <Box
              sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              }}
            >
              <FormControl fullWidth size="small" sx={fieldSx}>
                <InputLabel>Sponsorship status</InputLabel>
                <MuiSelect
                  label="Sponsorship status"
                  value={sponsorshipStatus}
                  onChange={(e) => {
                    setSponsorshipStatus(String(e.target.value));
                    markDirty();
                  }}
                >
                  {FUNDING_STATUSES.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </MuiSelect>
              </FormControl>
              <TextField
                fullWidth
                size="small"
                label="Sponsorship / approval contact"
                value={sponsorshipContact}
                onChange={(e) => {
                  setSponsorshipContact(e.target.value);
                  markDirty();
                }}
                sx={fieldSx}
              />
              <TextField
                fullWidth
                size="small"
                label="Execution / disbursement contact"
                value={sponsorshipExecution}
                onChange={(e) => {
                  setSponsorshipExecution(e.target.value);
                  markDirty();
                }}
                sx={fieldSx}
              />
            </Box>
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={2}
              label="Funding coordination note"
              value={sponsorshipNote}
              onChange={(e) => {
                setSponsorshipNote(e.target.value);
                markDirty();
              }}
              sx={{ ...fieldSx, mt: 2, mb: 2.5 }}
            />
            <Paper elevation={0} sx={{ p: 2.5, bgcolor: "#f8fafc", borderRadius: 3, border: "1px solid #e2e8f0" }}>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 800, color: "#0f172a", mb: 0.5 }}
              >
                Tentative Bulk Expense Estimate
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 2, color: "#64748b" }}
              >
                Enter planning estimates only. Blank means unknown, not zero
                or covered.
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "1fr 1fr",
                    lg: "1fr 1fr 1fr",
                  },
                }}
              >
                {BUDGET_LINE_KEYS.map((line) => (
                  <TextField
                    key={line.key}
                    fullWidth
                    size="small"
                    label={line.label}
                    value={budget[line.key]}
                    onChange={(e) => {
                      setBudget((prev) => ({
                        ...prev,
                        [line.key]: e.target.value,
                      }));
                      markDirty();
                    }}
                    helperText="Tentative"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    }}
                    sx={fieldSx}
                  />
                ))}
              </Box>
              <Box
                sx={{
                  mt: 2.5,
                  p: 1.5,
                  bgcolor: "#ffffff",
                  borderRadius: 2,
                  border: "1px solid #cbd5e1",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 700, color: "#475569" }}>
                  Estimated Total Budget:
                </Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#1d4ed8" }}>
                  {budgetTotal == null
                    ? "Not yet estimated"
                    : formatCurrency(budgetTotal)}
                </Typography>
              </Box>
            </Paper>
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={2}
              label="Budget assumptions and coverage notes"
              value={budgetNote}
              onChange={(e) => {
                setBudgetNote(e.target.value);
                markDirty();
              }}
              helperText="Clarify what is proposed, covered, partial, excluded, or still unknown."
              sx={{ ...fieldSx, mt: 2.5 }}
            />
          </SectionBlock>
        )}

        {/* Planning Oversight */}
        <SectionBlock
          id="section-oversight"
          title="Planning Oversight"
          subtitle={oversightSubtitle}
          icon={<Users size={20} />}
        >
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: {
                xs: "1fr",
                md: "minmax(220px, 0.75fr) minmax(280px, 1.25fr) auto",
              },
              alignItems: "start",
            }}
          >
            <FormControl fullWidth size="small" required sx={fieldSx}>
              <InputLabel>Oversight Type</InputLabel>
              <MuiSelect
                label="Oversight Type"
                value={supportType}
                onChange={(e) => {
                  setSupportType(e.target.value as SupportTypeCode);
                  markDirty();
                }}
              >
                {oversightOptions.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {SUPPORT_LABELS[opt]}
                  </MenuItem>
                ))}
              </MuiSelect>
              <FormHelperText>
                Options follow selected Retreat Type.
              </FormHelperText>
            </FormControl>
            <Box>
              <Select
                options={contactOptions}
                value={
                  contactOptions.find((o) => o.value === pendingContactValue) ||
                  null
                }
                onChange={(opt) => setPendingContactValue(opt?.value ?? null)}
                isClearable
                isSearchable
                isDisabled={
                  supportType === SUPPORT_TYPES.STEERING_COMMITTEE && !parishId
                }
                placeholder={oversightPickerPlaceholder}
                noOptionsMessage={() =>
                  supportType === SUPPORT_TYPES.STEERING_COMMITTEE && !parishId
                    ? "Select a parish first"
                    : supportType === SUPPORT_TYPES.STEERING_COMMITTEE
                      ? "No steering members for this parish"
                      : supportType === SUPPORT_TYPES.EXECUTIVE_COMMITTEE
                        ? "No super admins found"
                        : "No couples found"
                }
                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: 40,
                    fontSize: 14,
                    borderRadius: 8,
                    borderColor: "#cbd5e1",
                    "&:hover": { borderColor: "#94a3b8" },
                  }),
                }}
              />
              <FormHelperText sx={{ ml: 1.75 }}>
                {oversightPickerHelper}
              </FormHelperText>
            </Box>
            <Button
              variant="contained"
              size="small"
              startIcon={<Plus size={16} />}
              onClick={handleAddSupport}
              sx={{
                ...projectedSaveButtonSx,
                mt: 0.25,
                minWidth: 100,
                borderRadius: 2,
                py: 1,
              }}
            >
              Add
            </Button>
          </Box>
          {planningSupport.length === 0 ? (
            <Alert severity="warning" sx={{ mt: 2.5, borderRadius: 2.5 }}>
              No planning support contacts added yet.
            </Alert>
          ) : (
            <Stack spacing={1.25} sx={{ mt: 2.5 }}>
              {planningSupport.map((row, index) => {
                const email = resolveSupportEmail(row);
                return (
                  <Paper
                    key={`${row.userId || row.coupleId}-${row.supportType}-${index}`}
                    elevation={0}
                    sx={{
                      p: 2,
                      display: "flex",
                      gap: 1.5,
                      alignItems: "center",
                      bgcolor: "#f8fafc",
                      borderRadius: 2.5,
                      border: "1px solid #e2e8f0",
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 800, color: "#0f172a" }}>
                        {resolveSupportName(row) || "Unknown"}
                        {email ? ` (${email})` : ""}
                      </Typography>
                      <Chip
                        size="small"
                        label={
                          SUPPORT_LABELS[row.supportType as SupportTypeCode] ||
                          row.supportType
                        }
                        sx={{ mt: 0.75, bgcolor: "#eff6ff", color: "#1d4ed8", fontWeight: 700, borderRadius: 1.75 }}
                      />
                    </Box>
                    <Button
                      size="small"
                      color="error"
                      startIcon={<Trash2 size={14} />}
                      sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
                      onClick={() => {
                        setPlanningSupport((prev) =>
                          prev.filter((_, i) => i !== index),
                        );
                        markDirty();
                      }}
                    >
                      Delete
                    </Button>
                  </Paper>
                );
              })}
            </Stack>
          )}
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={3}
            label="Planning / Handoff Notes (optional)"
            value={planningNotes}
            onChange={(e) => {
              setPlanningNotes(e.target.value);
              markDirty();
            }}
            helperText="Record useful planning context for the future handoff."
            sx={{ ...fieldSx, mt: 2.5 }}
          />
        </SectionBlock>

        {/* Future Leadership */}
        <SectionBlock
          id="section-leadership"
          title="Future Leadership"
          subtitle="Assign the future Lead Couple when known. Assignment alone does not start Setup."
          icon={<Award size={20} />}
          badge={
            leadAssigned ? (
              <Chip
                icon={<CheckCircle2 size={14} />}
                label={eligibilityComplete ? "Leadership Verified" : "Review Pending"}
                color={eligibilityComplete ? "success" : "warning"}
                sx={{ fontWeight: 700, borderRadius: 2 }}
              />
            ) : undefined
          }
        >
          <Box sx={{ maxWidth: 620 }}>
            <Select
              options={coupleOptions}
              value={
                coupleOptions.find((o) => o.value === Number(leadCoupleId)) ||
                null
              }
              onChange={(opt) => {
                setLeadCoupleId(opt?.value ?? "");
                markDirty();
              }}
              isClearable
              isSearchable
              placeholder="Select Lead Couple…"
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: 42,
                  fontSize: 14,
                  borderRadius: 8,
                  borderColor: "#cbd5e1",
                  "&:hover": { borderColor: "#94a3b8" },
                }),
              }}
            />
            <FormHelperText sx={{ ml: 1.75, mt: 0.75 }}>
              Not required to save as Projected. Required to move this retreat
              to Setup.
            </FormHelperText>
          </Box>

          {leadAssigned && selectedLead && (
            <Box
              sx={{
                mt: 2.5,
                display: "grid",
                gap: 2.5,
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "minmax(280px, 0.85fr) minmax(420px, 1.15fr)",
                },
              }}
            >
              <Paper elevation={0} sx={{ p: 2.5, bgcolor: "#f8fafc", borderRadius: 3, border: "1px solid #e2e8f0" }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 1 }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#0f172a" }}>
                    Lead Couple Contact
                  </Typography>
                </Stack>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2, color: "#64748b", fontSize: "0.85rem" }}
                >
                  Shared Couple Master Data for retreat coordination.
                </Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#1e293b", mb: 1 }}>
                  {coupleShortName(selectedLead)}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Edit3 size={14} />}
                  sx={{
                    mb: 2.5,
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: 2,
                    borderColor: "#cbd5e1",
                    color: "#334155",
                    bgcolor: "#ffffff",
                    "&:hover": { borderColor: "#2563eb", bgcolor: "#eff6ff" },
                  }}
                  onClick={openLeadContactEdit}
                >
                  Update Contact Information
                </Button>
                {(
                  [
                    [
                      "Emails",
                      [selectedLead.hisEmail, selectedLead.herEmail]
                        .filter(Boolean)
                        .join(" · ") || "—",
                    ],
                    [
                      "Mailing address",
                      selectedLead.address || "No mailing address recorded.",
                    ],
                    [
                      "Contact preference or relevant note",
                      selectedLead.notes ||
                        "No contact preference note recorded.",
                    ],
                  ] as const
                ).map(([label, value]) => (
                  <Box key={label} sx={{ mb: 1.5 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 800,
                        color: "#64748b",
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                        fontSize: "0.68rem",
                      }}
                    >
                      {label}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: "#0f172a", mt: 0.25 }}>
                      {value}
                    </Typography>
                  </Box>
                ))}
              </Paper>

              <Paper elevation={0} sx={{ p: 2.5, bgcolor: "#f8fafc", borderRadius: 3, border: "1px solid #e2e8f0" }}>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 1 }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#0f172a" }}>
                    Eligibility Review Checklist
                  </Typography>
                  <Chip
                    size="small"
                    color={eligibilityComplete ? "success" : "warning"}
                    label={`${eligibilityCount} / ${ELIGIBILITY_ITEMS.length} Verified`}
                    sx={{ fontWeight: 700, borderRadius: 2 }}
                  />
                </Stack>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2, color: "#64748b", fontSize: "0.85rem" }}
                >
                  Manual-guided review required before Setup. Confirm an item
                  only when requirement is satisfied or authorized.
                </Typography>
                <Stack spacing={1.5}>
                  {ELIGIBILITY_ITEMS.map((item) => {
                    const state = eligibility[item.id];
                    return (
                      <Paper
                        key={item.id}
                        elevation={0}
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          bgcolor: state.checked ? "#f0fdf4" : "#ffffff",
                          border: "1px solid",
                          borderColor: state.checked ? "#bbf7d0" : "#e2e8f0",
                          transition: "all 0.15s ease",
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={state.checked}
                              onChange={(e) => {
                                setEligibility((prev) => ({
                                  ...prev,
                                  [item.id]: {
                                    ...prev[item.id],
                                    checked: e.target.checked,
                                  },
                                }));
                                markDirty();
                              }}
                              sx={{
                                color: "#94a3b8",
                                "&.Mui-checked": { color: "#16a34a" },
                              }}
                            />
                          }
                          label={
                            <Typography variant="body2" sx={{ fontWeight: 700, color: "#0f172a" }}>
                              {item.label}
                            </Typography>
                          }
                        />
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{ pl: 4.25, color: "#64748b", fontSize: "0.78rem" }}
                        >
                          {item.helper}
                        </Typography>
                        {!state.showNote ? (
                          <Button
                            size="small"
                            sx={{
                              ml: 4.25,
                              mt: 0.5,
                              textTransform: "none",
                              fontSize: "0.75rem",
                              fontWeight: 700,
                              color: "#2563eb",
                            }}
                            onClick={() =>
                              setEligibility((prev) => ({
                                ...prev,
                                [item.id]: {
                                  ...prev[item.id],
                                  showNote: true,
                                },
                              }))
                            }
                          >
                            + Add exception or supporting note
                          </Button>
                        ) : (
                          <TextField
                            fullWidth
                            size="small"
                            multiline
                            minRows={2}
                            placeholder="Document an exception, approval, supporting context, or follow-up"
                            value={state.note}
                            onChange={(e) => {
                              setEligibility((prev) => ({
                                ...prev,
                                [item.id]: {
                                  ...prev[item.id],
                                  note: e.target.value,
                                },
                              }));
                              markDirty();
                            }}
                            sx={{ ...fieldSx, ml: 4.25, mt: 1, width: "calc(100% - 34px)" }}
                          />
                        )}
                      </Paper>
                    );
                  })}
                </Stack>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ mt: 2, color: "#64748b" }}
                >
                  A note documents an exception request or approval; it does
                  not approve an exception by itself.
                </Typography>
              </Paper>
            </Box>
          )}
        </SectionBlock>

        {/* Next Stage */}
        {leadAssigned && !transitionSuccess && (
          <SectionBlock
            id="section-handoff"
            title="Next Stage"
            subtitle="Keep this retreat Projected, or deliberately prepare the Setup handoff."
            icon={<Rocket size={20} />}
          >
            <FormControl
              fullWidth
              size="small"
              sx={{ ...fieldSx, maxWidth: 420 }}
            >
              <InputLabel>Next Stage</InputLabel>
              <MuiSelect
                label="Next Stage"
                value={nextStage}
                onChange={(e) => {
                  setNextStage(String(e.target.value));
                  markDirty();
                }}
              >
                <MenuItem value="Keep as Projected">
                  Keep as Projected
                </MenuItem>
                <MenuItem value="Move to Retreats in Setup">
                  Move to Retreats in Setup
                </MenuItem>
              </MuiSelect>
            </FormControl>

            {nextStage === "Move to Retreats in Setup" && (
              <Paper
                variant="outlined"
                sx={{
                  mt: 1.5,
                  p: { xs: 1.5, sm: 2 },
                  bgcolor: HANDOFF_PANEL_BG,
                  border: `1px solid ${HANDOFF_PANEL_BORDER}`,
                  backgroundImage: "none",
                }}
              >
                <Typography variant="overline" color="primary.main">
                  Setup Handoff
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1.25 }}
                >
                  Review the people, planning details, prepared
                  notification, and effects of moving this retreat to Setup.
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "repeat(2, minmax(0, 1fr))",
                      lg: "repeat(4, minmax(0, 1fr))",
                    },
                    gap: 1.25,
                    mb: 1.5,
                  }}
                >
                  <HandoffSummaryCard
                    label="Retreat"
                    title={
                      parishDisplayName(selectedParish) || "Parish not selected"
                    }
                  >
                    {seasonYearLabel}
                    <br />
                    {weekendLabel}
                  </HandoffSummaryCard>
                  <HandoffSummaryCard
                    label="Lead Couple"
                    title={
                      selectedLead
                        ? coupleShortName(selectedLead)
                        : "Not assigned"
                    }
                  >
                    {leadEmail}
                    <br />
                    Retreat-specific Lead access intended after the Setup
                    transition
                  </HandoffSummaryCard>
                  <HandoffSummaryCard
                    label="Planning Contact"
                    title={primarySupportName}
                  >
                    {planningEmail}
                    <br />
                    Planning Contact access intended after the Setup
                    transition
                  </HandoffSummaryCard>
                  <HandoffSummaryCard
                    label="Access timing"
                    title="After confirmed Setup transition"
                  >
                    Assignments are recorded for both roles. Retreat-specific
                    access is intended to begin after the retreat enters Setup.
                  </HandoffSummaryCard>
                </Box>
                <Alert severity="info" sx={{ mb: 1.25 }}>
                  After the Projected Retreat moves to Retreats in Setup, the
                  Lead Couple and Planning Contact are intended to receive
                  retreat-specific access. This preview prepares the
                  assignments; it does not activate access.
                </Alert>
                <Box sx={{ mb: 1.25 }}>
                  <HandoffSummaryCard
                    label="Notification recipients"
                    title="Lead Couple and Assigned Planning Contact"
                  >
                    {recipientEmails}
                  </HandoffSummaryCard>
                </Box>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      lg: "minmax(260px, .65fr) minmax(440px, 1.35fr)",
                    },
                    gap: 1.25,
                  }}
                >
                  <TextField
                    fullWidth
                    size="small"
                    required
                    multiline
                    minRows={3}
                    label="Required Handoff Note"
                    value={transitionNote}
                    onChange={(e) => {
                      setTransitionNote(e.target.value);
                      markDirty();
                    }}
                    helperText="Summarize unresolved scheduling or venue details, planning ownership, committee status, exceptions needing follow-up, and the next Setup action."
                    sx={fieldSx}
                  />
                  <Box>
                    <TextField
                      fullWidth
                      size="small"
                      required
                      multiline
                      minRows={6}
                      label="Notification Message"
                      value={notificationMessage}
                      onChange={(e) => {
                        setNotificationMessage(e.target.value);
                        setNotificationEdited(true);
                        markDirty();
                      }}
                      helperText="Prepared for review only; no production email will be sent."
                      sx={fieldSx}
                    />
                    <Button
                      type="button"
                      size="small"
                      startIcon={<RefreshCw size={14} />}
                      sx={{ mt: 0.5, textTransform: "none" }}
                      onClick={() => rebuildNotification(true)}
                    >
                      Rebuild draft from current form data
                    </Button>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mt: 0.25 }}
                    >
                      Uses the current retreat, weekend, people, oversight, and
                      planning notes. Rebuilding replaces manual edits in this
                      message.
                    </Typography>
                  </Box>
                </Box>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  sx={{ mt: 1.5, alignItems: { sm: "center" } }}
                >
                  <Button
                    variant="contained"
                    startIcon={<Rocket size={16} />}
                    disabled={!retreatId || transitionMissing.length > 0}
                    onClick={() => setSetupConfirmModalOpen(true)}
                    sx={projectedSaveButtonSx}
                  >
                    {dirty
                      ? "Save & Review Setup Transition"
                      : "Review Setup Transition"}
                  </Button>
                  {(!retreatId || transitionMissing.length > 0) && (
                    <FormHelperText sx={{ m: 0 }}>
                      Complete valid Projected details, verify all Lead
                      eligibility items, and complete both handoff messages.
                    </FormHelperText>
                  )}
                </Stack>
              </Paper>
            )}
          </SectionBlock>
        )}

        {/* Bottom Save / Cancel Actions */}
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: 3.5,
            bgcolor: "#ffffff",
            border: "1px solid #e2e8f0",
            boxShadow: "0 4px 20px -2px rgba(15, 23, 42, 0.04)",
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            justifyContent="flex-end"
          >
            <Button
              variant="outlined"
              onClick={() => navigate("/manageRetreat")}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                borderRadius: 2.5,
                px: 3,
                py: 1,
                borderColor: "#cbd5e1",
                color: "#475569",
                "&:hover": { bgcolor: "#f8fafc", borderColor: "#94a3b8" },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={saving || Boolean(transitionSuccess)}
              onClick={handleSave}
              sx={{
                ...projectedSaveButtonSx,
                borderRadius: 2.5,
                px: 3.5,
                py: 1,
              }}
            >
              {saving ? "Saving…" : "Save Projected Retreat"}
            </Button>
          </Stack>
        </Paper>
      </Stack>
    </Box>
  </>
  );
}
